/**
 * Modal for uploading multiple files into one data-room category at once.
 * Each picked file must be mapped to a specific document slot before upload is
 * enabled. Files upload sequentially via onUploadItem; failures are counted and
 * reported rather than aborting the batch, and the modal closes only if all
 * succeed. Closing is blocked while an upload is in progress.
 */
import React, { useRef, useState } from 'react';

export default function BulkUploadModal({ category, onUploadItem, onClose }) {
  const inputRef = useRef(null);
  const [entries, setEntries]   = useState([]); // [{ uid, file, itemId }]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError]       = useState('');

  function addFiles(fileList) {
    const incoming = Array.from(fileList).map((file, i) => ({
      uid: `${file.name}-${Date.now()}-${i}`,
      file,
      itemId: null,
    }));
    setEntries((prev) => [...prev, ...incoming]);
  }

  function removeEntry(uid) {
    setEntries((prev) => prev.filter((e) => e.uid !== uid));
  }

  function assignItem(uid, val) {
    setEntries((prev) =>
      prev.map((e) => (e.uid === uid ? { ...e, itemId: val ? parseInt(val, 10) : null } : e))
    );
  }

  const unassigned = entries.filter((e) => e.itemId === null).length;
  const canSubmit  = entries.length > 0 && unassigned === 0 && !uploading;

  async function handleSubmit() {
    if (!canSubmit) return;
    setUploading(true);
    setError('');
    setProgress({ done: 0, total: entries.length });
    let failed = 0;
    for (const entry of entries) {
      try {
        await onUploadItem(entry.itemId, entry.file);
      } catch {
        failed++;
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setUploading(false);
    if (failed > 0) {
      setError(`${failed} file${failed > 1 ? 's' : ''} failed to upload. Please try again.`);
    } else {
      onClose();
    }
  }

  return (
    <div style={s.overlay} onClick={uploading ? undefined : onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        {!uploading && (
          <button style={s.close} onClick={onClose} aria-label="Close">✕</button>
        )}

        <div>
          <h2 style={s.title}>Bulk Upload</h2>
          <p style={s.subtitle}>{category.name}</p>
        </div>

        {/* Drop zone */}
        <div
          style={s.dropZone}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (!uploading) addFiles(e.dataTransfer.files); }}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => addFiles(e.target.files)}
          />
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
              stroke="var(--color-forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p style={s.dropText}>Click or drag files here to add them</p>
        </div>

        {/* File rows */}
        {entries.length > 0 && (
          <div style={s.list}>
            <div style={s.listHeader}>
              <span style={s.colLabel}>File</span>
              <span style={s.colLabel}>Assign to document</span>
              <span />
            </div>
            {entries.map((entry) => (
              <div key={entry.uid} style={s.row}>
                <span style={s.fileName} title={entry.file.name}>
                  {entry.file.name}
                </span>
                <select
                  style={{
                    ...s.select,
                    borderColor: entry.itemId ? 'var(--color-mint-dark)' : '#dc2626',
                    color: entry.itemId ? 'var(--color-forest)' : '#dc2626',
                  }}
                  value={entry.itemId ?? ''}
                  onChange={(e) => assignItem(entry.uid, e.target.value)}
                  disabled={uploading}
                >
                  <option value="">— assign to document —</option>
                  {category.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.number} {item.label}{item.status !== 'empty' ? ' ✓' : ''}
                    </option>
                  ))}
                </select>
                <button
                  style={s.removeBtn}
                  onClick={() => removeEntry(entry.uid)}
                  disabled={uploading}
                  title="Remove this file"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={s.footer}>
          {uploading ? (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}>
                <div style={{ ...s.progressFill, width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
              </div>
              <span style={s.progressLabel}>Uploading {progress.done} of {progress.total}…</span>
            </div>
          ) : (
            <>
              <div>
                {entries.length > 0 && unassigned > 0 && (
                  <p style={s.warn}>
                    {unassigned} file{unassigned !== 1 ? 's' : ''} still need{unassigned === 1 ? 's' : ''} to be assigned
                  </p>
                )}
                {error && <p style={s.errorText}>{error}</p>}
              </div>
              <button
                style={{ ...s.submitBtn, opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                Upload {entries.length > 0 ? `${entries.length} file${entries.length !== 1 ? 's' : ''}` : 'files'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 400,
    background: 'rgba(33,81,45,0.18)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  modal: {
    background: '#fff', borderRadius: 'var(--radius-lg)',
    boxShadow: '0 12px 48px rgba(33,81,45,0.22)',
    width: '100%', maxWidth: 700, maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    padding: '28px 32px', gap: 20, position: 'relative',
  },
  close: {
    position: 'absolute', top: 16, right: 16,
    background: 'none', border: 'none', fontSize: 16,
    color: 'var(--color-forest)', opacity: 0.5, cursor: 'pointer',
  },
  title: {
    fontFamily: 'var(--font-accent)', fontSize: 26, fontWeight: 700,
    color: 'var(--color-rust)', margin: 0,
  },
  subtitle: {
    fontFamily: 'var(--font-body)', fontSize: 15,
    color: 'var(--color-forest)', opacity: 0.6, marginTop: 4,
  },
  dropZone: {
    border: '2px dashed var(--color-mint-dark)', borderRadius: 'var(--radius-md)',
    padding: '20px 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 12, cursor: 'pointer',
    background: 'var(--color-frost)',
  },
  dropText: {
    fontFamily: 'var(--font-body)', fontSize: 14,
    color: 'var(--color-forest)', opacity: 0.6, margin: 0,
  },
  list: { overflowY: 'auto', maxHeight: 340, display: 'flex', flexDirection: 'column', gap: 6 },
  listHeader: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 36px',
    gap: 10, padding: '0 4px 6px',
    borderBottom: '1px solid var(--color-mint-dark)',
  },
  colLabel: {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
    color: 'var(--color-forest)', opacity: 0.5,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  row: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 36px',
    gap: 10, alignItems: 'center',
    background: 'var(--color-frost)', borderRadius: 'var(--radius-sm)',
    padding: '8px 10px',
  },
  fileName: {
    fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  select: {
    fontFamily: 'var(--font-body)', fontSize: 13,
    background: '#fff', border: '1.5px solid',
    borderRadius: 'var(--radius-sm)', padding: '5px 8px',
    width: '100%', outline: 'none', cursor: 'pointer',
  },
  removeBtn: {
    background: 'none', border: 'none',
    color: 'var(--color-forest)', opacity: 0.4,
    fontSize: 13, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, width: 28, height: 28, borderRadius: 4,
  },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 16, paddingTop: 4, borderTop: '1px solid var(--color-mint-dark)',
  },
  progressWrap: { flex: 1, display: 'flex', alignItems: 'center', gap: 14 },
  progressTrack: {
    flex: 1, height: 8, background: 'var(--color-mint-dark)',
    borderRadius: 4, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: 'var(--color-forest)',
    borderRadius: 4, transition: 'width 0.3s ease',
  },
  progressLabel: {
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
    color: 'var(--color-forest)', flexShrink: 0,
  },
  warn:      { fontFamily: 'var(--font-body)', fontSize: 13, color: '#d97706', margin: 0 },
  errorText: { fontFamily: 'var(--font-body)', fontSize: 13, color: '#dc2626', margin: 0 },
  submitBtn: {
    fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
    color: '#fff', background: 'var(--color-forest)',
    border: 'none', borderRadius: 'var(--radius-sm)',
    padding: '10px 24px', flexShrink: 0,
  },
};
